import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { theme } from '../styles/theme';
import ByteBotLogo from './ByteBotLogo';

function AuthAction() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [validCode, setValidCode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [actionType, setActionType] = useState('');
  const [hasVerified, setHasVerified] = useState(false);
  const { confirmNewPassword, verifyResetCode, applyActionCodeForAuth, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the oobCode and mode from URL params
  const searchParams = new URLSearchParams(location.search);
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const continueUrl = searchParams.get('continueUrl');

  useEffect(() => {
    const verifyCode = async () => {
      // Prevent re-verification
      if (hasVerified || !oobCode || !mode) {
        setVerifying(false);
        return;
      }

      setActionType(mode);
      setHasVerified(true);

      try {
        if (mode === 'resetPassword') {
          const userEmail = await verifyResetCode(oobCode);
          setEmail(userEmail);
          setValidCode(true);
        } else if (mode === 'verifyEmail') {
          // For email verification, apply the code directly
          await applyActionCodeForAuth(oobCode);
          setValidCode(true);
          setSuccess(true);
        } else if (mode === 'recoverEmail') {
          // Handle email recovery
          await applyActionCodeForAuth(oobCode);
          setValidCode(true);
          setSuccess(true);
        } else if (mode === 'verifyAndChangeEmail') {
          // Handle email change verification
          await applyActionCodeForAuth(oobCode);
          setValidCode(true);
          setSuccess(true);
        } else {
          // Unknown mode
          setValidCode(false);
        }
      } catch (error) {
        console.error('Code verification error:', error);
        setValidCode(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, mode, hasVerified, verifyResetCode, applyActionCodeForAuth]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await confirmNewPassword(oobCode, newPassword);
      setSuccess(true);
      // Removed automatic navigation - user will click button when ready
    } catch (error) {
      console.error('Password reset error:', error);
    }
    setLoading(false);
  };

  const handleEmailVerification = () => {
    // For email verification, redirect to continue URL or dashboard
    if (continueUrl) {
      window.location.href = continueUrl;
    } else {
      navigate('/dashboard');
    }
  };

  const getActionTitle = () => {
    switch (actionType) {
      case 'resetPassword':
        return '[NEW PASSWORD]';
      case 'verifyEmail':
        return '[EMAIL VERIFIED]';
      case 'recoverEmail':
        return '[EMAIL RECOVERY]';
      case 'verifyAndChangeEmail':
        return '[EMAIL CHANGED]';
      default:
        return '[AUTH ACTION]';
    }
  };

  const getActionSubtitle = () => {
    switch (actionType) {
      case 'resetPassword':
        return 'Enter your new password for this account.';
      case 'verifyEmail':
        return 'Your email address has been successfully verified.';
      case 'recoverEmail':
        return 'Your email address has been recovered.';
      case 'verifyAndChangeEmail':
        return 'Your email address change has been confirmed.';
      default:
        return 'Processing authentication action...';
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      padding: '20px',
    },
    card: {
      backgroundColor: theme.colors.surface,
      padding: '40px',
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.borderDark}`,
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center',
    },
    header: {
      marginBottom: '30px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoIcon: {
      marginRight: theme.spacing.sm,
      display: 'flex',
      alignItems: 'center',
    },
    logoAccent: {
      color: theme.colors.primary,
    },
    title: {
      fontSize: theme.typography.fontSizes['3xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    subtitle: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.lg,
    },
    emailDisplay: {
      backgroundColor: theme.colors.background,
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.md,
      marginBottom: theme.spacing.lg,
      fontSize: theme.typography.fontSizes.sm,
      color: theme.colors.text,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.lg,
    },
    input: {
      padding: '15px',
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Inter', sans-serif",
      backgroundColor: theme.colors.background,
      transition: 'all 0.2s ease',
      color: theme.colors.text,
    },
    inputFocus: {
      outline: 'none',
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.surface,
    },
    button: {
      padding: '15px',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      backgroundColor: theme.colors.primary,
      color: '#fff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    error: {
      color: theme.colors.primary,
      textAlign: 'center',
      padding: theme.spacing.md,
      backgroundColor: theme.colors.primary + '10',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    success: {
      color: '#10b981',
      textAlign: 'center',
      padding: theme.spacing.md,
      backgroundColor: '#10b98110',
      border: '2px solid #10b981',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    loading: {
      color: theme.colors.textLight,
      textAlign: 'center',
      padding: theme.spacing.lg,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    switchText: {
      textAlign: 'center',
      marginTop: theme.spacing.lg,
      color: theme.colors.textLight,
      fontSize: theme.typography.fontSizes.sm,
    },
    switchLink: {
      color: theme.colors.primary,
      textDecoration: 'none',
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    backLink: {
      display: 'inline-block',
      marginBottom: theme.spacing.lg,
      color: theme.colors.textLight,
      textDecoration: 'none',
      fontSize: theme.typography.fontSizes.sm,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
  };

  // Loading state while verifying code
  if (verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color={theme.colors.primary}
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </div>
          </div>
          <div style={styles.loading}>
            [VERIFYING ACTION CODE...]
          </div>
        </div>
      </div>
    );
  }

  // Invalid code or wrong mode
  if (!validCode || !mode || !oobCode) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <Link to="/login" style={styles.backLink}>← [BACK TO LOGIN]</Link>
          
          <div style={styles.header}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color={theme.colors.primary}
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </div>
            <h1 style={styles.title}>[INVALID LINK]</h1>
            <p style={styles.subtitle}>
              This authentication link is invalid or has expired.
            </p>
          </div>

          <div style={styles.error}>
            [ERROR] Invalid or expired authentication link
          </div>

          <div style={styles.switchText}>
            <Link to="/forgot-password" style={styles.switchLink}>
              [REQUEST NEW RESET LINK]
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state for non-password reset actions
  if (success && actionType !== 'resetPassword') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color={theme.colors.primary}
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </div>
            <h1 style={styles.title}>[SUCCESS]</h1>
            <p style={styles.subtitle}>
              {getActionSubtitle()}
            </p>
          </div>

          <div style={styles.success}>
            [SUCCESS] {actionType === 'verifyEmail' ? 'Email verified!' : 'Action completed!'}
          </div>

          <button
            onClick={handleEmailVerification}
            style={styles.button}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            [CONTINUE TO DASHBOARD]
          </button>

          <div style={styles.switchText}>
            <Link to="/login" style={styles.switchLink}>
              [GO TO LOGIN]
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state for password reset
  if (success && actionType === 'resetPassword') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color={theme.colors.primary}
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </div>
            <h1 style={styles.title}>[SUCCESS]</h1>
            <p style={styles.subtitle}>
              Your password has been reset successfully.
            </p>
          </div>

          <div style={styles.success}>
            [SUCCESS] Password updated successfully!
          </div>

          <button
            onClick={() => navigate('/login')}
            style={styles.button}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            [RETURN TO LOGIN]
          </button>
        </div>
      </div>
    );
  }

  // Password reset form
  if (actionType === 'resetPassword') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <Link to="/login" style={styles.backLink}>← [BACK TO LOGIN]</Link>
          
          <div style={styles.header}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color={theme.colors.primary}
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </div>
            <h1 style={styles.title}>{getActionTitle()}</h1>
            <p style={styles.subtitle}>
              {getActionSubtitle()}
            </p>
            
            {email && (
              <div style={styles.emailDisplay}>
                [ACCOUNT: {email}]
              </div>
            )}
          </div>

          {error && (
            <div style={styles.error}>
              [ERROR] {error}
            </div>
          )}

          <form onSubmit={handlePasswordReset} style={styles.form}>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              style={styles.input}
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => Object.assign(e.target.style, styles.input)}
            />
            
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              style={styles.input}
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => Object.assign(e.target.style, styles.input)}
            />

            <button
              type="submit"
              disabled={loading}
              style={styles.button}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.colors.primaryDark;
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme.colors.primary;
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {loading ? '[UPDATING PASSWORD...]' : '[UPDATE PASSWORD]'}
            </button>
          </form>

          <div style={styles.switchText}>
            Remember your password?{' '}
            <Link to="/login" style={styles.switchLink}>
              [BACK TO LOGIN]
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Fallback for other action types
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <Link to="/login" style={styles.backLink}>← [BACK TO LOGIN]</Link>
        
        <div style={styles.header}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <ByteBotLogo 
                size={32} 
                color={theme.colors.primary}
              />
            </div>
            [BYTE<span style={styles.logoAccent}>BOT</span>]
          </div>
          <h1 style={styles.title}>{getActionTitle()}</h1>
          <p style={styles.subtitle}>
            {getActionSubtitle()}
          </p>
        </div>

        <div style={styles.success}>
          [SUCCESS] Action completed successfully!
        </div>

        <div style={styles.switchText}>
          <Link to="/dashboard" style={styles.switchLink}>
            [GO TO DASHBOARD]
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AuthAction; 